import {
  findNodePath,
  flattenTreeToBoxes,
  resolveImageLayerPreview,
  resolveTextLayerPreview,
} from "./layerTree";
import { listArtboardIDs, matchPresetID } from "./artboards";
import { isContinuousLayout, isCarouselLayout } from "./slides";
import { resolveInlineReplacementUrls } from "./textInlineReplacements";

const SQUARE_PRESET_ID = "square";

const buildArtboardPreview = ({ artboard, assets }) => {
  const width = Number(artboard?.width) || 0;
  const height = Number(artboard?.height) || 0;
  const nodes = artboard?.nodes || {};
  if (!width || !height || Object.keys(nodes).length === 0) return null;

  const boxes = flattenTreeToBoxes({
    nodes,
    nodeLayouts: artboard.nodeLayouts || {},
    artboardWidth: width,
    artboardHeight: height,
  }).map((box) => {
    const imagePreview = resolveImageLayerPreview({ box, assets });
    const textPreview = resolveTextLayerPreview({ box });
    return {
      ...box,
      assetUrl: imagePreview.assetUrl,
      text: textPreview.text,
      shape:
        box.type === "shape"
          ? findNodePath({ nodes, nodeID: box.id })?.node?.shape || null
          : null,
      inlineReplacementUrls: resolveInlineReplacementUrls({
        replacements: box.style?.inlineReplacements,
        assets,
      }),
    };
  });

  if (boxes.length === 0) return null;
  return { width, height, boxes };
};

/**
 * Builds the static boxes needed to paint a template layout as a thumbnail.
 * Prefers an exact square, then the format closest to 1:1. Returns null when
 * layers. Returns null when nothing can be reconstructed (no layers,
 * continuous strips, missing doc) so callers can fall back to the stored image.
 */
export const buildTemplateLayoutPreview = ({ doc } = {}) => {
  if (!doc) return null;
  const layoutKind = doc.layoutKind;
  const slides = doc.slides || [];
  if (isContinuousLayout({ layoutKind, slides })) return null;

  const artboards = isCarouselLayout({ layoutKind, slides })
    ? slides[0]?.artboards || {}
    : doc.artboards || {};
  const assets = doc.assets || {};

  const orderedIDs = listArtboardIDs({ artboards });
  const aspectDistance = (id) => {
    if (
      id === SQUARE_PRESET_ID ||
      matchPresetID(artboards[id] || {}) === SQUARE_PRESET_ID
    ) {
      return 0;
    }
    const width = Number(artboards[id]?.width) || 0;
    const height = Number(artboards[id]?.height) || 0;
    if (width <= 0 || height <= 0) return Number.POSITIVE_INFINITY;
    return Math.abs(width / height - 1);
  };
  const prioritizedIDs = [...orderedIDs].sort(
    (left, right) => aspectDistance(left) - aspectDistance(right)
  );

  for (const id of prioritizedIDs) {
    const preview = buildArtboardPreview({ artboard: artboards[id], assets });
    if (preview) return preview;
  }
  return null;
};
